import React, { useMemo, useRef } from 'react'
import { formatUnixToDate } from '_utils/formatNumber'
import { useToasts } from 'react-toast-notifications'
import uploadApi from '_api/upload'
import userApi from '_api/user'
import reportApi from '_api/report'
import { setUserInfo } from '_utils/localData'
import { FaRegEdit } from 'react-icons/fa'

const HeroCard = ({ currentUser = {}, onUserUpdated, isPremium = false }) => {
  const { addToast } = useToasts()
  const fileInputRef = useRef(null)

  const { name = 'User', photoUrl, createdAt, groups = [] } = currentUser
  const primaryGroup = groups?.[0]
  const groupName = primaryGroup?.name || 'Co-bee'
  const joinDate = primaryGroup?.joinDate ?? createdAt

  const formattedDate = useMemo(() => {
    if (!joinDate) return ''
    const normalizedTimestamp =
      typeof joinDate === 'number' && joinDate > 10000000000
        ? Math.floor(joinDate / 1000)
        : joinDate
    return formatUnixToDate(normalizedTimestamp)
  }, [joinDate])

  const getInitials = (str) => {
    if (!str) return 'U'
    const parts = str.trim().split(/\s+/)
    if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase()
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }

  const handleAvatarUpload = async (e) => {
    const { files } = e.target
    if (!files || !files.length) return
    try {
      const { msgCode, msgResp } = await uploadApi.uploadPhoto(files)
      if (msgCode % 100 === 0 && msgResp?.url) {
        const newPhotoUrl = msgResp.url
        await userApi.updateUser({ photoUrl: newPhotoUrl })
        const { msgResp: updatedUser } = await userApi.getUser()
        setUserInfo(updatedUser)
        if (onUserUpdated) onUserUpdated(updatedUser)
        addToast('Profile picture updated successfully', { appearance: 'success', autoDismiss: true })
      }
    } catch (error) {
      reportApi.report({ message: `[Avatar upload error]: ${error?.msgResp || error?.message}` })
      addToast(error?.msgResp || 'Failed to upload photo', { appearance: 'error', autoDismiss: true })
    }
  }

  const triggerAvatarInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click()
    }
  }

  return (
    <div className='profile-hero'>
      <div className='profile-card'>
        {/* Cover strip */}
        <div className='profile-card-cover'>
          <div className='banner-pattern'></div>
        </div>

        {/* Avatar */}
        <div className='profile-card-body'>
          <div className='profile-avatar-wrap' onClick={triggerAvatarInput} title='Change profile picture'>
            <div className='profile-avatar'>
              {photoUrl ? (
                <img src={photoUrl} alt={name} />
              ) : (
                <span>{getInitials(name)}</span>
              )}
            </div>
            <div className='avatar-edit-badge' title='Upload new photo'>
              <FaRegEdit size={11} color='#ffffff' />
            </div>
            <input
              type='file'
              ref={fileInputRef}
              accept='image/*'
              style={{ display: 'none' }}
              onChange={handleAvatarUpload}
            />
          </div>

          <div className='profile-name'>{name}</div>
          {(formattedDate || groupName) && (
            <div className='profile-sub'>
              {formattedDate ? `Member since ${formattedDate}` : ''}
              {formattedDate && groupName ? ' · ' : ''}
              {groupName}
            </div>
          )}
          {isPremium && (
            <div
              className='profile-badge profile-badge--premium'
              style={{ background: '#7B40F2' }}
            >
              <svg
                width='10'
                height='10'
                viewBox='0 0 24 24'
                fill='none'
                stroke='currentColor'
                strokeWidth='2.5'
                strokeLinecap='round'
                strokeLinejoin='round'
              >
                <polygon points='12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2' />
              </svg>
              Premium
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default HeroCard
